import { FormattedDate, FormattedTime, FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import { H2, Text, Table, Td, Th, Tr, Loading } from '../../components';
import { FormattedDuration } from '../../lib/util';
import {
  settings_system,
  penaltiesQuery,
  PenaltyStatus
} from '../../core-types';
import { useQuery } from '@apollo/client';
import { gql } from '@apollo/client';
import { GraphQLErrors } from '../../components/GraphQLErrors';

const TableRow = styled(Tr)<{ resolved: boolean }>`
  text-decoration: ${props => (props.resolved ? 'line-through' : 'none')};
`;

export const PENALTIES_QUERY = gql`
  query penaltiesQuery {
    currentUser {
      id
      penaltyCount
      currentSubscription {
        id
        penalties {
          id
          value
          createdAt
          trip {
            id
            startDock {
              id
              dockGroup {
                id
                title
              }
            }
            endDock {
              id
              dockGroup {
                id
                title
              }
            }
            startedAt
            endedAt
            duration
          }
          resolvedAt
        }
      }
    }
  }
`;

function ProfilePenaltiesWarningDescriptionMessage({
  severe = false,
  penaltiesCount = 0
}: {
  severe?: boolean;
  penaltiesCount?: number;
}) {
  if (severe) {
    return (
      <FormattedMessage
        id="profile_penalties_severe_warning_description"
        defaultMessage="You have {penaltyCount} penalties. One more penalty will result in account suspension."
        values={{
          penaltyCount: penaltiesCount,
          profile_penalties_severe_warning_description: penaltiesCount
        }}
      />
    );
  }
  return (
    <FormattedMessage
      id="profile_penalties_warning_description"
      defaultMessage="You have {penaltyCount} penalties. Multiple penalties will result in account suspension."
      values={{
        penaltyCount: penaltiesCount,
        profile_penalties_warning_description: penaltiesCount
      }}
    />
  );
}

function PenaltyMessage({
  penaltyStatus,
  penaltiesCount = 0
}: {
  penaltyStatus: PenaltyStatus;
  penaltiesCount?: number;
}) {
  switch (penaltyStatus) {
    case PenaltyStatus.blocked: {
      return (
        <FormattedMessage
          id="modules.Subscription.Penalties.blocked"
          defaultMessage="Your account is suspended because of too many penalties."
        />
      );
    }
    case PenaltyStatus.none: {
      return (
        <FormattedMessage
          id="modules.Subscription.Penalties.none"
          defaultMessage="You have no unresolved penalties"
        />
      );
    }
    case PenaltyStatus.severe_warning: {
      return (
        <ProfilePenaltiesWarningDescriptionMessage
          penaltiesCount={penaltiesCount}
          severe
        />
      );
    }
    case PenaltyStatus.warning: {
      return (
        <ProfilePenaltiesWarningDescriptionMessage
          penaltiesCount={penaltiesCount}
        />
      );
    }
  }
}

export const Penalties = ({
  system,
  penaltyStatus
}: {
  system: settings_system;
  penaltyStatus: PenaltyStatus;
}) => {
  const { data, loading, error } = useQuery<penaltiesQuery>(PENALTIES_QUERY, {
    ssr: false
  });

  const penalties = data?.currentUser?.currentSubscription?.penalties;

  if (error && !penalties) {
    return <GraphQLErrors error={error} />;
  }

  const penaltiesCount =
    data?.currentUser?.penaltyCount || penalties?.length || 0;

  return (
    <>
      <H2>
        <FormattedMessage id="penalties" defaultMessage="Penalties" />
      </H2>
      <Text as="p">
        <PenaltyMessage
          penaltyStatus={penaltyStatus}
          penaltiesCount={penaltiesCount}
        />
      </Text>
      {loading && <Loading />}
      {penalties && penalties.length > 0 && (
        <Table>
          <thead>
            <Tr>
              <Th>
                <FormattedMessage id="date" defaultMessage="Date" />
              </Th>
              <Th>
                <FormattedMessage
                  id="modules.Subscription.Penalties.table.thead.startDockGroup"
                  defaultMessage="Start station"
                />
              </Th>
              <Th>
                <FormattedMessage
                  id="modules.Subscription.Penalties.table.thead.endDockGroup"
                  defaultMessage="End station"
                />
              </Th>
              <Th>
                <FormattedMessage
                  id="modules.Subscription.Penalties.table.thead.aboveLimit"
                  defaultMessage="Above limit"
                />
              </Th>
            </Tr>
          </thead>
          <tbody>
            {penalties.map(penalty => {
              if (!penalty) {
                return null;
              }
              const { trip } = penalty;
              const aboveLimit =
                system.standardRentalMins &&
                trip &&
                trip.duration !== null &&
                trip.duration !== undefined &&
                trip.duration / 60 - system.standardRentalMins;

              return (
                <TableRow key={penalty.id} resolved={!!penalty.resolvedAt}>
                  <Td>
                    {penalty.createdAt && (
                      <FormattedDate
                        value={penalty.createdAt}
                        year="numeric"
                        month="long"
                        day="2-digit"
                      />
                    )}
                  </Td>
                  <Td>
                    {trip && (
                      <>
                        {trip.startDock && trip.startDock.dockGroup.title} (
                        {trip.startedAt && (
                          <FormattedTime
                            hour="numeric"
                            minute="numeric"
                            value={trip.startedAt}
                          />
                        )}
                        )
                      </>
                    )}
                  </Td>
                  <Td>
                    {trip && (
                      <>
                        {trip.endDock && trip.endDock.dockGroup.title} (
                        {trip.endedAt && (
                          <FormattedTime
                            hour="numeric"
                            minute="numeric"
                            value={trip.endedAt}
                          />
                        )}
                        )
                      </>
                    )}
                  </Td>
                  <Td>
                    {aboveLimit && (
                      <FormattedDuration durationMins={aboveLimit} />
                    )}
                  </Td>
                </TableRow>
              );
            })}
          </tbody>
        </Table>
      )}
    </>
  );
};
